import {
  mysqlTable,
  int,
  bigint,
  varchar,
  text,
  date,
  datetime,
  boolean,
  timestamp,
  mysqlEnum,
  index,
  decimal,
  double,
  type AnyMySqlColumn,
} from "drizzle-orm/mysql-core";
import { companies } from "./tenancy";
import { users } from "./identity";
import { projects } from "./projects";
import { teams } from "./hr";

/**
 * Performance module (OKRs / goals / reviews / meetings). Ported from
 * legacy-worksuite/TodaysWork-main/Modules/Performance/Database/Migrations/ by reconciling
 * each table's Schema::create with every up() Schema::table ALTER (down() ignored):
 *  - objectives: project_id added after id (2024_12_17_080606); +created_by, department_id, goal_type FKs.
 *  - performance_settings: created without company_id then company_id added after id (2025_01_01);
 *    purchase_code/supported_until/notify_update were never on this table (only dropped-if-exist, no-op);
 *    +create_meeting_roles/manager/view_meeting_* (2025_01_02); +create_meeting_participant (2025_01_16).
 * NOTE legacy uses `integer unsigned` for objectives/goal_types/key_results_metrics/key_results/check_ins/
 *  performance_settings.company_id but `unsignedBigInteger` would be wrong — these are int unsigned per PHP.
 *  performance_meetings.company_id is `unsignedInteger` (int unsigned) too. company_id maps to int unsigned.
 * Deferred cross-batch FKs (not companies/users/own-file): objectives.project_id → projects,
 *  objectives.department_id → teams (HR), objectives.goal_type → goal_types (own-file, kept).
 */

/** goal_types — 2024_09_22_061728. */
export const goalTypes = mysqlTable("goal_types", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  type: mysqlEnum("type", ["individual", "department", "company"]).default("individual").notNull(),
  viewByOwner: boolean("view_by_owner").default(false).notNull(),
  manageByOwner: boolean("manage_by_owner").default(false).notNull(),
  viewByManager: boolean("view_by_manager").default(false).notNull(),
  manageByManager: boolean("manage_by_manager").default(false).notNull(),
  viewByRoles: text("view_by_roles"),
  manageByRoles: text("manage_by_roles"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** key_results_metrics — 2024_09_22_061728. */
export const keyResultsMetrics = mysqlTable("key_results_metrics", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * objectives — 2024_09_22_061728 + project_id added after id (2024_12_17_080606).
 * goal_type → goal_types (own-file). department_id → teams (HR). project_id → projects.
 */
export const objectives = mysqlTable(
  "objectives",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    // FK → projects
    projectId: int("project_id", { unsigned: true }).references(() => projects.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    title: varchar("title", { length: 255 }).notNull(),
    description: text("description"),
    goalType: bigint("goal_type", { mode: "number", unsigned: true }).references(
      () => goalTypes.id,
      { onDelete: "cascade" },
    ),
    // FK → teams (HR)
    departmentId: int("department_id", { unsigned: true }).references(() => teams.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    startDate: date("start_date").notNull(),
    endDate: date("end_date").notNull(),
    priority: mysqlEnum("priority", ["low", "medium", "high"]).default("low").notNull(),
    checkInFrequency: mysqlEnum("check_in_frequency", [
      "daily",
      "weekly",
      "bi-weekly",
      "monthly",
      "quarterly",
    ])
      .default("weekly")
      .notNull(),
    scheduleOn: varchar("schedule_on", { length: 255 }),
    rotationDate: int("rotation_date"),
    sendCheckInReminder: boolean("send_check_in_reminder").default(true).notNull(),
    createdBy: int("created_by", { unsigned: true }).references(() => users.id, {
      onUpdate: "cascade",
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("objectives_department_id_foreign").on(t.departmentId),
    index("objectives_users_id_foreign").on(t.createdBy),
  ],
);

/** objective_owners — 2024_09_22_061728. No timestamps. owner_id → users. */
export const objectiveOwners = mysqlTable("objective_owners", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  objectiveId: bigint("objective_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => objectives.id, { onDelete: "cascade" }),
  ownerId: int("owner_id", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
  }),
});

/** key_results — 2024_09_22_061728. objective_id → objectives. metrics_id → key_results_metrics (no FK in legacy, only index). */
export const keyResults = mysqlTable(
  "key_results",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    objectiveId: bigint("objective_id", { mode: "number", unsigned: true })
      .notNull()
      .references(() => objectives.id),
    title: varchar("title", { length: 255 }).notNull(),
    description: text("description"),
    metricsId: int("metrics_id", { unsigned: true }),
    targetValue: double("target_value"),
    currentValue: double("current_value"),
    originalCurrentValue: double("original_current_value"),
    keyPercentage: double("key_percentage"),
    lastCheckIn: date("last_check_in"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("key_results_metrics_id_foreign").on(t.metricsId)],
);

/** check_ins — 2024_09_22_061728. key_result_id → key_results. check_in_by → users. */
export const checkIns = mysqlTable(
  "check_ins",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    keyResultId: bigint("key_result_id", { mode: "number", unsigned: true })
      .notNull()
      .references(() => keyResults.id),
    progressUpdate: text("progress_update").notNull(),
    currentValue: double("current_value"),
    objectivePercentage: decimal("objective_percentage", { precision: 8, scale: 2 }).notNull(),
    confidenceLevel: mysqlEnum("confidence_level", ["low", "medium", "high"])
      .default("low")
      .notNull(),
    barriers: text("barriers"),
    checkInDate: datetime("check_in_date"),
    checkInBy: int("check_in_by", { unsigned: true }).references(() => users.id, {
      onUpdate: "cascade",
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("check_ins_users_id_foreign").on(t.checkInBy)],
);

/** objective_progress_statuses — 2024_09_22_061728. objective_id → objectives. */
export const objectiveProgressStatuses = mysqlTable("objective_progress_statuses", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  objectiveId: bigint("objective_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => objectives.id, { onDelete: "cascade" }),
  objectiveProgress: decimal("objective_progress", { precision: 5, scale: 2 }).notNull(),
  timeLeftPercentage: decimal("time_left_percentage", { precision: 5, scale: 2 }).notNull(),
  status: mysqlEnum("status", ["onTrack", "atRisk", "offTrack", "completed"]).notNull(),
  color: mysqlEnum("color", ["success", "warning", "danger", "primary"]).notNull(),
  condition: text("condition").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * performance_meetings — 2025_01_03_085054. company_id is unsignedInteger (int unsigned).
 * parent_id self-ref. objective_id → objectives. meeting_for/meeting_by/added_by → users.
 */
export const performanceMeetings = mysqlTable("performance_meetings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  parentId: bigint("parent_id", { mode: "number", unsigned: true }).references(
    (): AnyMySqlColumn => performanceMeetings.id,
    { onDelete: "cascade" },
  ),
  objectiveId: bigint("objective_id", { mode: "number", unsigned: true }).references(
    () => objectives.id,
    { onDelete: "cascade" },
  ),
  startDateTime: datetime("start_date_time").notNull(),
  endDateTime: datetime("end_date_time").notNull(),
  repeat: mysqlEnum("repeat", ["yes", "no"]).default("no").notNull(),
  repeatEvery: mysqlEnum("repeat_every", ["day", "week", "month", "year"]),
  repeatCycles: int("repeat_cycles"),
  repeatType: mysqlEnum("repeat_type", ["after", "on"]),
  untilDate: date("until_date"),
  meetingFor: int("meeting_for", { unsigned: true })
    .notNull()
    .references(() => users.id, { onUpdate: "cascade", onDelete: "cascade" }),
  meetingBy: int("meeting_by", { unsigned: true })
    .notNull()
    .references(() => users.id, { onUpdate: "cascade", onDelete: "cascade" }),
  addedBy: int("added_by", { unsigned: true })
    .notNull()
    .references(() => users.id, { onUpdate: "cascade", onDelete: "cascade" }),
  status: mysqlEnum("status", ["pending", "completed", "cancelled"]).default("pending").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** performance_meeting_agenda — 2025_01_03_085054. meeting_id → performance_meetings. added_by → users. */
export const performanceMeetingAgenda = mysqlTable("performance_meeting_agenda", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  meetingId: bigint("meeting_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => performanceMeetings.id, { onDelete: "cascade" }),
  discussionPoint: text("discussion_point").notNull(),
  addedBy: int("added_by", { unsigned: true })
    .notNull()
    .references(() => users.id, { onUpdate: "cascade", onDelete: "cascade" }),
  isDiscussed: mysqlEnum("is_discussed", ["yes", "no"]).default("no").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** performance_meeting_actions — 2025_01_03_085054. meeting_id → performance_meetings. added_by → users. */
export const performanceMeetingActions = mysqlTable("performance_meeting_actions", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  meetingId: bigint("meeting_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => performanceMeetings.id, { onDelete: "cascade" }),
  actionPoint: text("action_point").notNull(),
  addedBy: int("added_by", { unsigned: true })
    .notNull()
    .references(() => users.id, { onUpdate: "cascade", onDelete: "cascade" }),
  isActioned: mysqlEnum("is_actioned", ["yes", "no"]).default("no").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * performance_settings — 2024_09_19_082743 (created WITHOUT company_id; company_id NOT in original
 * create — added 2025_01_01_082743 after id). +meeting role cols (2025_01_02), +create_meeting_participant
 * (2025_01_16). The original create's company_id IS present in the create block; the 2025_01_01 ALTER
 * is a hasColumn-guarded no-op. company_id is int unsigned.
 */
export const performanceSettings = mysqlTable("performance_settings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  sendNotification: mysqlEnum("send_notification", ["yes", "no"]).default("no").notNull(),
  createMeetingRoles: text("create_meeting_roles"),
  createMeetingManager: boolean("create_meeting_manager").default(false).notNull(),
  createMeetingParticipant: boolean("create_meeting_participant").default(false).notNull(),
  viewMeetingRoles: text("view_meeting_roles"),
  viewMeetingManager: boolean("view_meeting_manager").default(false).notNull(),
  viewMeetingParticipant: boolean("view_meeting_participant").default(false).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** performance_global_settings — 2024_09_19_082743. No company_id (global, single row). */
export const performanceGlobalSettings = mysqlTable("performance_global_settings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  purchaseCode: varchar("purchase_code", { length: 255 }),
  licenseType: varchar("license_type", { length: 20 }),
  purchasedOn: timestamp("purchased_on"),
  supportedUntil: timestamp("supported_until"),
  notifyUpdate: boolean("notify_update").default(true).notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});
